/**
 * The mansard's wedge (the app's primitive 1495) built here rather than loaded, so the openings of its wall can
 * cut it (`src/state/attic.ts` says where), and a roof window turned into its slope.
 *
 * The wedge keeps the model's frame: x across its width from its middle, y up from its bottom (the knee), and
 * its depth along +z from its back — the model's pivot is the top of that back edge — or along −z for a wedge a
 * project turned round (`AtticFrame.zSign`). Its five faces are the model's material slots: top, left, right,
 * back and slope; what a cut opens up inside it takes the slope's finish, as the room's surface running on.
 */
import * as THREE from 'three';
import type { Item, Level, Opening, Wall } from '../../core/hp/types';
import { atticCuts, atticFrame, slopeDepthAt, type AtticCut, type AtticFrame } from '../../state/attic';
import { materialFor } from './buildScene';
import type { TextureImageCache } from './TextureImageCache';

export const ATTIC_SLOTS = ['top', 'left', 'right', 'back', 'slope'] as const;
type Slot = typeof ATTIC_SLOTS[number];
const EPS = 1e-6;

type Band = [number, number];
/** [0, h] less the given stretches, merged and in order. */
function kept(h: number, removed: Band[]): Band[] {
  const cuts = removed.map(([a, b]) => [Math.max(0, a), Math.min(h, b)] as Band).filter(([a, b]) => b - a > EPS).sort((p, q) => p[0] - q[0]);
  const out: Band[] = [];
  let from = 0;
  for (const [a, b] of cuts) {
    if (a > from + EPS) out.push([from, a]);
    from = Math.max(from, b);
  }
  if (h > from + EPS) out.push([from, h]);
  return out;
}
/** The stretches of `a` not in `b`. */
function minus(a: Band[], b: Band[]): Band[] {
  return a.flatMap(([from, to]) => kept(to - from, b.map(([p, q]) => [p - from, q - from] as Band)).map(([p, q]) => [p + from, q + from] as Band));
}

/**
 * The wedge `w` wide, `h` high and `d` deep, less `cuts` (across its width `a0`–`a1`, up from its bottom `y0`–`y1`,
 * through its whole depth). One group per slot, in `ATTIC_SLOTS` order; flat normals; UVs spanning each face 0–1.
 */
export function atticGeometry(w: number, h: number, d: number, cuts: readonly Pick<AtticCut, 'a0' | 'a1' | 'y0' | 'y1'>[] = [], zSign: 1 | -1 = 1): THREE.BufferGeometry {
  const byslot = new Map<Slot, { position: number[]; normal: number[]; uv: number[] }>(ATTIC_SLOTS.map(slot => [slot, { position: [], normal: [], uv: [] }]));
  const depthAt = (y: number) => slopeDepthAt({ height: h, depth: d }, y);
  const uvOf = (slot: Slot, a: number, n: number, y: number): [number, number] =>
    slot === 'top' ? [a / w + .5, n / d] : slot === 'left' || slot === 'right' ? [n / d, y / h] : [a / w + .5, y / h];
  /** A flat convex face through wedge points (a, n, y), turned to face `normal` (in wedge terms), fanned into triangles. */
  const face = (points: Array<[number, number, number]>, normal: [number, number, number], slot: Slot, uv: (a: number, n: number, y: number) => [number, number] = (a, n, y) => uvOf(slot, a, n, y)) => {
    const unique = points.filter((p, i) => { const q = points[(i + points.length - 1) % points.length]; return Math.hypot(p[0] - q[0], p[1] - q[1], p[2] - q[2]) > EPS; });
    if (unique.length < 3) return;
    const local = unique.map(([a, n, y]) => new THREE.Vector3(a, y, n * zSign));
    const want = new THREE.Vector3(normal[0], normal[2], normal[1] * zSign).normalize();
    const newell = new THREE.Vector3();
    for (let i = 0; i < local.length; i++) {
      const p = local[i], q = local[(i + 1) % local.length];
      newell.x += (p.y - q.y) * (p.z + q.z); newell.y += (p.z - q.z) * (p.x + q.x); newell.z += (p.x - q.x) * (p.y + q.y);
    }
    if (newell.lengthSq() < 1e-12) return;
    const order = newell.dot(want) < 0 ? [...unique.keys()].reverse() : [...unique.keys()];
    const target = byslot.get(slot)!;
    for (let i = 1; i + 1 < order.length; i++) for (const k of [order[0], order[i], order[i + 1]]) {
      const v = local[k], [a, n, y] = unique[k];
      target.position.push(v.x, v.y, v.z); target.normal.push(want.x, want.y, want.z); target.uv.push(...uv(a, n, y));
    }
  };
  const slopeNormal: [number, number, number] = [0, h, -d];
  const trapezoid = ([ya, yb]: Band): Array<[number, number]> => [[0, ya], [depthAt(ya), ya], [depthAt(yb), yb], [0, yb]];

  // Across the width the wedge falls into stretches between the cuts' sides, each with its own bands of height left.
  const xs = [...new Set([-w / 2, w / 2, ...cuts.flatMap(cut => [cut.a0, cut.a1]).map(a => Math.max(-w / 2, Math.min(w / 2, a)))])].sort((p, q) => p - q)
    .filter((a, i, all) => i === 0 || a - all[i - 1] > EPS);
  const stretches = xs.slice(1).map((a1, i) => {
    const a0 = xs[i], middle = (a0 + a1) / 2;
    return { a0, a1, bands: kept(h, cuts.filter(cut => cut.a0 < middle && cut.a1 > middle).map(cut => [cut.y0, cut.y1] as Band)) };
  });
  for (const { a0, a1, bands } of stretches) {
    for (const band of bands) {
      const [ya, yb] = band, na = depthAt(ya), nb = depthAt(yb);
      face([[a0, na, ya], [a1, na, ya], [a1, nb, yb], [a0, nb, yb]], slopeNormal, 'slope');
      face([[a0, 0, ya], [a1, 0, ya], [a1, 0, yb], [a0, 0, yb]], [0, -1, 0], 'back');
      if (yb >= h - EPS) face([[a0, 0, h], [a1, 0, h], [a1, d, h], [a0, d, h]], [0, 0, 1], 'top');
      else face([[a0, 0, yb], [a1, 0, yb], [a1, nb, yb], [a0, nb, yb]], [0, 0, 1], 'slope', (a, n) => [a / w + .5, n / d]);
      if (ya > EPS) face([[a0, 0, ya], [a1, 0, ya], [a1, na, ya], [a0, na, ya]], [0, 0, -1], 'slope', (a, n) => [a / w + .5, n / d]);
    }
  }
  const cap = (a: number, bands: Band[], toward: 1 | -1, slot: Slot) => {
    for (const band of bands) face(trapezoid(band).map(([n, y]) => [a, n, y] as [number, number, number]), [toward, 0, 0], slot,
      slot === 'slope' ? (_a, n, y) => [n / d, y / h] : undefined);
  };
  if (stretches.length) {
    cap(stretches[0].a0, stretches[0].bands, -1, 'left');
    cap(stretches[stretches.length - 1].a1, stretches[stretches.length - 1].bands, 1, 'right');
  }
  for (let i = 1; i < stretches.length; i++) {
    const before = stretches[i - 1], after = stretches[i], at = after.a0;
    cap(at, minus(before.bands, after.bands), 1, 'slope');
    cap(at, minus(after.bands, before.bands), -1, 'slope');
  }

  const geometry = new THREE.BufferGeometry(), position: number[] = [], normal: number[] = [], uv: number[] = [];
  ATTIC_SLOTS.forEach((slot, index) => {
    const part = byslot.get(slot)!;
    geometry.addGroup(position.length / 3, part.position.length / 3, index);
    position.push(...part.position); normal.push(...part.normal); uv.push(...part.uv);
  });
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(position, 3));
  geometry.setAttribute('normal', new THREE.Float32BufferAttribute(normal, 3));
  geometry.setAttribute('uv', new THREE.Float32BufferAttribute(uv, 2));
  geometry.computeBoundingBox(); geometry.computeBoundingSphere();
  return geometry;
}

/** What makes a wedge look as it does: its size, its turn, and where the openings of its wall go through it. */
export function atticShape(item: Item, level: Pick<Level, 'walls' | 'openings'>): { frame: AtticFrame; cuts: AtticCut[] } {
  const frame = atticFrame(item, level.walls);
  return { frame, cuts: atticCuts(level, item, frame) };
}

/**
 * The mansard's node, as the viewer places an item (`placeItem`): at its position and turn, its bottom at its
 * elevation. It is `real` for the resize and the plan's footprint, which measure its geometry, and `generated`, so
 * it is disposed of rather than returned to the model library.
 */
export function atticNode(item: Item, level: Pick<Level, 'walls' | 'openings'>, options: { maxAnisotropy: number; textureImages?: TextureImageCache }): THREE.Mesh {
  const { frame, cuts } = atticShape(item, level);
  const geometry = atticGeometry(item.size.w, item.size.h, item.size.d, cuts, frame.zSign);
  const materials = ATTIC_SLOTS.map(slot => {
    const material = materialFor(item.materials?.[slot] ?? item.materials?.['*'], { maxAnisotropy: options.maxAnisotropy, textureImages: options.textureImages,
      fallbackColor: 0xf6f5f3, explicitTiling: true });
    material.name = slot;
    // It faces down into the room as the ceiling does, and takes the ceiling's bounce surrogate (buildScene).
    material.emissive.copy(material.color); material.emissiveIntensity = .22;
    return material;
  });
  const mesh = new THREE.Mesh(geometry, materials);
  mesh.castShadow = true; mesh.receiveShadow = true;
  mesh.position.set(item.position.x, item.elevation, item.position.z);
  mesh.rotation.y = -item.rotation;
  mesh.name = `item:${item.id}`;
  mesh.userData = { kind: 'item', itemId: item.id, caption: item.caption, model: item.modelRef.raw, real: true, generated: true };
  return mesh;
}

/**
 * A window lying in a mansard's slope. The model `buildOpeningModel` stood upright in the wall — its holder at the
 * wall's middle and the sill, x along the wall, y up, z towards the room — is turned about its bottom edge into
 * the slope and drawn out along it, so it covers the slope over the same heights it covers the wall, with the
 * middle of its depth on the slope: its frame covers the edges of the slope's hole, as a roof window's does.
 */
export function tiltIntoSlope(holder: THREE.Object3D, opening: Opening, wall: Wall, frame: AtticFrame, lift = 0): void {
  const tilt = Math.atan2(frame.depth, frame.height), cos = Math.cos(tilt);
  if (!(frame.height > EPS) || !(frame.depth > EPS)) return;
  const face = wall.thickness / 2, n = frame.inward;
  // The bottom edge's middle on the slope: the wall's face at the window's middle, then into the room as far as the slope is.
  const bottom = slopeDepthAt(frame, opening.sill - frame.knee);
  // `lift` out of the room, square to the slope: where a slope of the room's ceiling has its lining end (src/render/three/slope.ts).
  const rise = frame.height / frame.depth, out = lift / Math.hypot(1, rise);
  holder.position.set(wall.start.x + wall.dir.x * opening.offset + n.x * (face + bottom - rise * out), opening.sill + out,
    wall.start.z + wall.dir.z * opening.offset + n.z * (face + bottom - rise * out));
  holder.rotation.set(tilt, Math.atan2(n.x, n.z), 0, 'YXZ');
  holder.scale.set(1, 1 / cos, 1);
  holder.userData = { ...holder.userData, roofWindow: true };
}
